import { foundation, dataPanel } from '../../ui';

const fc = foundation.color;

/** ONE empty-state icon tile (RD-UI-192): a rounded square, radii.md, 40px / 20px glyph.
 *  Used by 'standard' and 'stacked' here and by RouteDayEmptyState (export). */
export const EMPTY_TILE_PX = 40;
export const EMPTY_TILE_ICON_PX = 20;

/**
 * Atlas EmptyState primitive.
 *
 * tone:
 *   'brand'   (default) — tinted chip; for "nothing exists yet" states.
 *   'muted'             — neutral chip; for "no search/filter matches".
 *   'success'           — green chip; for POSITIVE empties ("empty because
 *                         you're done" — billing's "Todo al día." precedent).
 *
 * ONE ICON TILE (RD-UI-192): every empty state draws its icon in the same rounded
 * square (radii.md, 40px, 20px glyph) — never a circle, never a white tile with a
 * shadow. brand (blue tint) = nothing exists yet; muted (grey) = a quiet day or
 * a search/filter that found nothing (RouteDayEmptyState is muted).
 *
 * variant:
 *   'standard' (default) — centered panel (icon / title / message / button).
 *   'inline'             — compact row for INSIDE dashboard widgets:
 *                          28px chip + stacked title/message + quiet CTA link.
 *                          Transparent (no dataPanel chrome) so it sits in any card.
 *   'stacked'            — the roomy form of 'inline' for a card body with nothing to
 *                          list (KAN-946 "every state looks designed"): a 36px chip
 *                          over a centred title and a one-line message. Transparent,
 *                          same tones + icon canon; no action (a card's header link
 *                          already is the way out).
 *
 * action.variant (standard only): 'primary' (default) | 'tertiary'.
 * For inline, `action` renders as a quiet brand link (min 40px tap target).
 *
 * action.busy (BOTH variants): the request this action started is in flight.
 * The button stays mounted and focusable but stops firing, dims, and reports
 * aria-busy. Use it whenever the caller swaps in its own "…ing" label, so the
 * control is not announced as actionable while inert.
 *
 * ICON CANON (one lucide icon per domain — do not drift; audited 2026-07-31,
 * estimates + credits corrected 2026-08-15):
 *   jobs/visits = Briefcase · invoices = Receipt · documents = FileText · estimates = ClipboardList
 *   customers = Contact · team = Users · activity/audit = Logs · schedule/dates = CalendarDays
 *   couldn't load = CloudOff (LoadErrorState, tone muted) · route = Route · AI = Sparkles · success/positive empty = CheckCircle2 · credits = CircleDollarSign
 *
 *   FilePen is RESERVED FOR "DRAFT" and is never a domain icon. It carries that
 *   meaning in invoiceStatusTokens, estimateStatusTokens, jobsBillingStatusTokens
 *   and jobInvoiceStatusMeta, and is the Drafts segment pill. This canon used to
 *   read "estimates = FilePen", which put the draft glyph on a whole feature area
 *   — visible in the Invoices tab, where Drafts and Estimates showed the same
 *   icon at once. Estimates is ClipboardList everywhere now: sidebar nav, bottom
 *   tab bar, segment hero, the dashboard's Recent estimates card and this component's own
 *   estimates empty states (which already used ClipboardList — the doc was the
 *   stale part, not the code).
 *
 *   Credits moved CreditCard → CircleDollarSign so the segment pill, the hero
 *   tile and the empty state all agree; CreditCard reads as a payment method,
 *   not as money the tenant is holding.
 *   mail family keeps real distinctions: Mail (email) / MailPlus (invites) /
 *   Inbox (generic feeds) / Send (sent items)
 *   filter/search no-match = the domain's icon with tone="muted" (not a new icon)
 * `touch` (optional, default false): standard-variant action gets a 44px min
 * height. Callers pass `isTouch` (useResponsiveLayout, KAN-970).
 * Ambient per-day empties on route/schedule boards use RouteDayEmptyState, not
 * this component — two sanctioned patterns, each with its own role.
 */
export default function EmptyState({ icon: Icon, title, message, action, tone = 'brand', variant = 'standard', touch = false }) {
  // Shared by BOTH variants — an inline action must honour `busy` too, or a
  // caller passing it to the inline variant silently gets a live button with no
  // error and no failing test.
  const busyGuardedClick = (e) => {
    if (action?.busy) return;
    action?.onClick?.(e);
  };
  const chip = {
    brand: { bg: fc.brand.primaryMuted, color: fc.brand.text },
    muted: { bg: fc.neutral.surfaceMuted, color: fc.neutral.textMuted },
    success: { bg: fc.semantic.successSoft, color: fc.semantic.success },
  }[tone] || { bg: fc.brand.primaryMuted, color: fc.brand.text };

  if (variant === 'inline') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: foundation.spacePx[3], padding: `${foundation.spacePx[3]} 0` }}>
        {Icon && (
          <span style={{
            width: 28, height: 28, borderRadius: foundation.radii.md, backgroundColor: chip.bg,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon size={14} style={{ color: chip.color }} strokeWidth={1.7} aria-hidden />
          </span>
        )}
        <span style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <span style={{
              display: 'block', fontSize: foundation.fontSize.sm,
              fontWeight: foundation.fontWeight.bold, color: fc.neutral.textSecondary,
            }}>{title}</span>
          )}
          {message && (
            <span style={{
              display: 'block', fontSize: foundation.fontSize.xs,
              color: fc.neutral.textMuted, marginTop: '1px', lineHeight: 1.45,
            }}>{message}</span>
          )}
        </span>
        {action && (
          <button
            type="button"
            onClick={busyGuardedClick}
            // `busy` is honoured here too. Applying it to the standard branch
            // ONLY would leave an inline caller with a live, clickable button
            // mid-flight — no error, no warning, no failing test: the silent
            // wiring gap this codebase keeps getting bitten by.
            aria-disabled={action.busy || undefined}
            aria-busy={action.busy || undefined}
            style={{
              appearance: 'none', WebkitAppearance: 'none', border: 'none', background: 'transparent',
              padding: `10px ${foundation.spacePx[1]}`, minHeight: '40px',
              cursor: action.busy ? 'not-allowed' : 'pointer',
              opacity: action.busy ? 0.65 : 1,
              fontSize: foundation.fontSize.xs, fontWeight: foundation.fontWeight.semibold,
              color: fc.brand.text, whiteSpace: 'nowrap', flexShrink: 0,
              fontFamily: foundation.fontFamily.sans, WebkitTapHighlightColor: 'transparent',
            }}
          >{action.label}</button>
        )}
      </div>
    );
  }

  if (variant === 'stacked') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        gap: foundation.spacePx[2], padding: `${foundation.spacePx[3]} 0`,
      }}
      >
        {Icon && (
          <span style={{
            width: EMPTY_TILE_PX, height: EMPTY_TILE_PX, borderRadius: foundation.radii.md, backgroundColor: chip.bg,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon size={EMPTY_TILE_ICON_PX} style={{ color: chip.color }} strokeWidth={1.8} aria-hidden />
          </span>
        )}
        {title && (
          <span style={{
            display: 'block', fontSize: foundation.fontSize.sm, lineHeight: 1.35,
            fontWeight: foundation.fontWeight.bold, color: fc.neutral.textSecondary,
          }}>{title}</span>
        )}
        {message && (
          <span style={{
            display: 'block', maxWidth: '280px', fontSize: foundation.fontSize.xs,
            color: fc.neutral.textMuted, lineHeight: 1.45, textWrap: 'balance',
          }}>{message}</span>
        )}
      </div>
    );
  }

  const btnVariant = action?.variant ?? 'primary';
  const touchMin = touch ? { minHeight: '44px' } : null;
  const buttonStyle = btnVariant === 'tertiary' ? {
    marginTop: foundation.spacePx[1],
    padding: `${foundation.spacePx[2]} ${foundation.spacePx[4]}`,
    fontSize: foundation.fontSize.sm,
    fontWeight: foundation.fontWeight.semibold,
    color: fc.brand.text,
    backgroundColor: fc.brand.primaryMuted,
    border: `1px solid ${fc.brand.primary}`,
    borderRadius: foundation.radii.sm,
    cursor: 'pointer',
    transition: 'background-color 150ms cubic-bezier(0.16, 1, 0.3, 1)',
  } : {
    marginTop: foundation.spacePx[1],
    padding: `10px 18px`,
    fontSize: foundation.fontSize.sm,
    fontWeight: 600,
    color: fc.brand.onPrimary,
    backgroundColor: fc.brand.primary,
    border: 'none',
    borderRadius: foundation.radii.md,
    cursor: 'pointer',
    transition: 'background-color 150ms cubic-bezier(0.16, 1, 0.3, 1)',
  };

  return (
    <div style={{
      ...dataPanel, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: `${foundation.spacePx[8]} ${foundation.spacePx[5]}`,
      textAlign: 'center', gap: foundation.spacePx[3],
    }}
    >
      {Icon && (
        <div style={{
          width: EMPTY_TILE_PX, height: EMPTY_TILE_PX, borderRadius: foundation.radii.md,
          backgroundColor: chip.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={EMPTY_TILE_ICON_PX} style={{ color: chip.color }} strokeWidth={1.8} aria-hidden />
        </div>
      )}
      {title && (
        <p style={{
          margin: 0, fontSize: foundation.fontSize.lg,
          fontWeight: foundation.fontWeight.bold,
          color: fc.neutral.text,
        }}>{title}</p>
      )}
      {message && (
        <p style={{
          margin: 0, fontSize: foundation.fontSize.sm,
          color: fc.neutral.textMuted,
          maxWidth: '320px', lineHeight: 1.5,
        }}>{message}</p>
      )}
      {action && (
        <button
          type="button"
          onClick={busyGuardedClick}
          // `busy` marks the action in-flight: the button stays mounted (so its
          // width and the card's layout do not shift) but stops accepting input
          // and announces itself as busy. Without it, a caller rendering its own
          // "…ing" label leaves a keyboard or screen-reader user being told the
          // control is actionable while it is inert. Optional — omitting it
          // preserves the previous behaviour exactly.
          //
          // aria-disabled, NOT disabled: a real `disabled` attribute makes the
          // node unfocusable, so the browser blurs it to <body> the instant a
          // request starts. A keyboard user who just pressed Enter loses their
          // place and never hears the aria-busy they were disabled for — the
          // change would defeat the accessibility it exists to provide. The
          // click is neutralized in the handler instead.
          aria-disabled={action.busy || undefined}
          aria-busy={action.busy || undefined}
          style={{ ...buttonStyle, ...touchMin, ...(action.busy ? { opacity: 0.65, cursor: 'not-allowed' } : null) }}
          data-cvx-press
        >{action.label}</button>
      )}
    </div>
  );
}
